import React, {useEffect, useRef, useState} from 'react';
import {
    findAllPdvProductsByQueryString,
    findPdvPendingSalesByCustomerIdList,
    insertPdvSale
} from "../../utils/requestUtils";
import {makeRequest} from "../../utils/httpRequest";
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import {useNavigate, useParams} from "react-router-dom";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import {showAlert, showConfirm} from "../../core/dialog";

const apiUrl = Config.apiUrl;

const PdvFinishSaleItems = () => {
    const {customerIdsParam} = useParams();
    const [customers, setCustomers] = useState([]);
    const [originalSales, setOriginalSales] = useState([]);
    const [sales, setSales] = useState([]);
    const [products, setProducts] = useState([]);
    const [originalProducts, setOriginalProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [queryString, setQueryString] = useState('');
    const inputQueryString = useRef(null);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();
    const [totalValue, setTotalValue] = useState(undefined);

    async function updateSales(customerIds) {
        const allProducts = await findAllPdvProductsByQueryString('') || [];
        let pendingSales = [];
        for (const id of customerIds) {
            const found = originalSales.filter(s => s.customer?.id === id);
            pendingSales = pendingSales.concat(found);
        }

        const productQuantities = {};
        pendingSales.forEach(sale => {
            sale.saleItems.forEach(item => {
                const key = item.product.id + (item.productName || '');
                if (productQuantities[key]) {
                    productQuantities[key].quantity += item.quantity;
                } else {
                    productQuantities[key] = {
                        id: item.product.id,
                        productName: item.productName,
                        salePrice: item.product.salePrice,
                        quantity: item.quantity
                    };
                }
            });
        });

        allProducts.forEach((item, index) => {
            const key = item.id + item.name;
            if (!productQuantities[key]) {
                productQuantities[key] = {id: item.id, productName: item.name, salePrice: item.salePrice, quantity: 0, order: index};
            }
        });

        let productList = Object.values(productQuantities).map(pq => ({
            id: pq.id, companyId: 0, code: 0, name: pq.productName, salePrice: pq.salePrice,
            category: '', quantity: pq.quantity, order: pq.order
        })).sort((a, b) => (a.order || 0) - (b.order || 0));

        const withItems = productList.filter(p => p.quantity > 0 || p.quantity < 0);
        const withoutItems = productList.filter(p => !p.quantity);
        productList = withItems.concat(withoutItems);

        const total = productList.reduce((acc, p) => {
            if (p.quantity > 0 || p.quantity < 0) return acc + (p.salePrice || 0) * (p.quantity || 0);
            return acc;
        }, 0);
        setTotalValue(total);
        setSales(pendingSales);
        setOriginalProducts(productList);
        setProducts(JSON.parse(JSON.stringify(productList)));
        setFilteredProducts(JSON.parse(JSON.stringify(productList)));
    }

    async function makeQuery() {
        try {
            const customerIds = customerIdsParam.split(',').map(Number);
            let newCustomers = [];
            const allCustomers = await makeRequest(apiUrl + '/api/customers/query=', 'GET') || [];
            for (const id of customerIds) {
                const customer = allCustomers.find(c => c.id === id);
                if (customer) { customer.checked = true; newCustomers.push(customer); }
            }
            const ids = newCustomers.filter(c => c.checked).map(c => c.id);
            const pendingSales = await findPdvPendingSalesByCustomerIdList(ids) || [];
            setOriginalSales(pendingSales);
            setCustomers(newCustomers);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    function compareArrays(array1, array2) {
        const differences = [];
        const productMap = new Map();
        array1.forEach(p => productMap.set(p.id + p.name, p));
        array2.forEach(p => {
            const key = p.id + p.name;
            const orig = productMap.get(key);
            if (orig && p.quantity !== orig.quantity) {
                differences.push({id: p.id, name: p.name, quantity: p.quantity - orig.quantity});
            }
        });
        return differences;
    }

    useEffect(() => {
        setLoading(true);
        makeQuery();
    }, []);

    useEffect(() => {
        const total = products.reduce((acc, p) => {
            if (p.quantity > 0 || p.quantity < 0) return acc + (p.salePrice || 0) * (p.quantity || 0);
            return acc;
        }, 0);
        setTotalValue(total);
    }, [products]);

    function removeSpecialChars(str) {
        return str.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    }

    const handleInputChange = (e) => {
        setQueryString(removeSpecialChars(e.target.value).toUpperCase());
    };

    useEffect(() => {
        const filtered = products.filter(f => removeSpecialChars(f.name).toUpperCase().includes(queryString));
        setFilteredProducts(JSON.parse(JSON.stringify(filtered)));
    }, [queryString]);

    const doConfirm = async () => {
        const checkedCustomers = customers.filter(c => c.checked);
        if (checkedCustomers.length === 0) { showAlert('Nenhuma comanda selecionada.'); return; }
        if (!await showConfirm('Essa operação não poderá ser revertida. Deseja continuar?')) return;

        await makeRequest(apiUrl + '/api/receipts/list', 'POST',
            {'Content-Type': 'application/json'}, JSON.stringify(checkedCustomers));

        const differences = compareArrays(originalProducts, products);
        const saleItemsWithDifferences = differences.map(p => ({product: p, productName: p.name, quantity: p.quantity}));

        if (saleItemsWithDifferences.length > 0) {
            const diffSale = {
                customerName: 'DIF-' + checkedCustomers[0].name,
                saleItems: saleItemsWithDifferences.map(s => ({...s, status: 2}))
            };
            await insertPdvSale(diffSale);
        }

        const salesId = sales.map(s => s.id);
        await makeRequest(apiUrl + '/api/pdv-sales/update-status-payed', 'PUT',
            {'Content-Type': 'application/json'}, JSON.stringify(salesId));

        let urlToGo = '/pdv/table';
        const unchecked = customers.filter(c => !c.checked);
        if (unchecked.length > 0) {
            unchecked.forEach(c => c.checked = true);
            setCustomers(unchecked);
            urlToGo = `/pdv/finishitems/${unchecked.map(c => c.id).join(',')}`;
        }
        navigate(urlToGo);
    };

    const doPrint = async () => {
        const checkedCustomers = customers.filter(c => c.checked);
        if (checkedCustomers.length === 0) { showAlert('Nenhuma comanda selecionada.'); return; }
        const salesId = sales.map(s => s.id);
        await makeRequest(apiUrl + '/api/pdv-sales/print-bill', 'PUT',
            {'Content-Type': 'application/json'}, JSON.stringify(salesId));
    };

    const onCheckBoxChange = (index) => {
        const updated = [...customers];
        updated[index].checked = !updated[index].checked;
        setCustomers(updated);
    };

    useEffect(() => {
        setLoading(true);
        if (customers.length > 0) {
            updateSales(customers.filter(c => c.checked).map(c => c.id)).then(() => setLoading(false));
        }
    }, [customers]);

    return (
        <>
            <div className="header-label">Fechar Comandas</div>
            {customers.map((customer, index) => (
                <div key={index} style={{display: 'flex', alignItems: 'center'}}>
                    <input type="checkbox" checked={customer.checked} onChange={() => onCheckBoxChange(index)}/>
                    {customer.name}
                </div>
            ))}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}
                 className="input-container">
                <input style={{width: '100%'}} ref={inputQueryString} type="text" value={queryString}
                       onChange={handleInputChange} onFocus={(e) => e.target.select()}/>
                <button id="clearButton" onClick={() => { setQueryString(''); inputQueryString.current.focus(); }}>X</button>
            </div>
            <table style={{width: '100%'}}>
                <thead>
                <tr>
                    <th>Nome</th>
                    <th>Preço</th>
                    <th/><th/><th/>
                </tr>
                </thead>
                <tbody>
                {filteredProducts.map((product, index) => (
                    <tr key={index} style={{backgroundColor: product.quantity > 0 ? 'var(--warning-color)' : product.quantity < 0 ? 'var(--error-color)' : 'white'}}>
                        <td><div>{product.name}</div></td>
                        <td>
                            <div style={{textAlign: 'right'}}>{(product.salePrice || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        </td>
                        <td>
                            <button className="remove-button" onClick={() => {
                                setProducts(prev => prev.map(item => item.id === product.id && item.name === product.name
                                    ? {...item, quantity: (item.quantity || 0) - 1} : item));
                                setFilteredProducts(prev => prev.map((item, i) => i === index
                                    ? {...item, quantity: (item.quantity || 0) - 1} : item));
                            }}>-</button>
                        </td>
                        <td>{product.quantity || 0}</td>
                        <td>
                            <button onClick={() => {
                                setProducts(prev => prev.map(item => item.id === product.id && item.name === product.name
                                    ? {...item, quantity: (item.quantity || 0) + 1} : item));
                                setFilteredProducts(prev => prev.map((item, i) => i === index
                                    ? {...item, quantity: (item.quantity || 0) + 1} : item));
                            }}>+</button>
                        </td>
                    </tr>
                ))}
                </tbody>
                {filteredProducts.length === 0 && <tfoot><tr><td colSpan={5}>Nenhum item encontrado</td></tr></tfoot>}
            </table>
            <div style={{marginTop: '5px'}}>
                <div className="total-label" style={{textAlign: 'right', fontSize: '30px', color: '#cc5545'}}>
                    Valor Total: R$ {(totalValue || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
            </div>
            <ActionButtonsManagerView>
                <button onClick={async () => { setLoading(true); await doPrint(); setLoading(false); }} disabled={loading}>Imprimir</button>
                <button onClick={async () => { setLoading(true); await doConfirm(); setLoading(false); }} disabled={loading}>Confirmar</button>
            </ActionButtonsManagerView>
        </>
    );
};

export default PdvFinishSaleItems;
